import React, { useMemo } from 'react';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import EditImage from '../../../../../../ImageManager/Edit';
import useSlideShowImageFetcher from '../../../../../Utils/useSlideShowImageFetcher';

const CustomEditImageModal = ({ editorInstance, modalManager, slideObject, imageModelToEdit }) => {
  const { fetchImages } = useSlideShowImageFetcher();
  const isAdmin = useSlideShowContext(state => state.isAdmin);
  const locale = useSlideShowContext(state => state.locale);
  const updateSlideObject = useSlideShowContext(state => state.updateSlideObject);
  const imgModel = useMemo(() => {
    if (!imageModelToEdit) return null;

    const idMatch = imageModelToEdit.match(/\d+/);
    if (!idMatch) return null;

    // AIDEV-NOTE: Try matching by ID first, then fall back to matching by snippet to handle manual HTML edits
    const byId = slideObject.imageModels.find(im => im.id.toString() === idMatch[0].toString());
    if (byId) return byId;

    return slideObject.imageModels.find(im => im.snippet === imageModelToEdit);
  }, [slideObject, imageModelToEdit]);

  const replaceImage = (imageData) => {
    const image = $(`img[data-snippet="${imageModelToEdit}"]`);

    if (image.length === 0) {
      return;
    }
    const { image: { imageUrl, imageEsUrl, descriptionEn, descriptionEs } } = imageData;

    const src = locale === 'es' && imageEsUrl && !imageEsUrl.includes('missing.png') ? imageEsUrl : imageUrl;
    const description = locale === 'es' ? descriptionEs || descriptionEn : descriptionEn;

    const imageToUpdate = image[1] || image[0];
    // @ts-ignore
    imageToUpdate.src = src;
    // @ts-ignore
    imageToUpdate.alt = description || '';
    imageToUpdate.dataset.zoomurl = src;

    // AIDEV-NOTE: Add or remove the zoom wrapper to match the updated zoomType so present mode
    // doesn't need to mutate the DOM structure when rendering the zoom icon.
    const parent = imageToUpdate.parentElement;
    if (!parent) return;

    const hasWrapper = parent.classList.contains('image-zoom-icon-wrapper');

    if (imageData.zoomType && !hasWrapper) {
      const wrapper = document.createElement('div');
      wrapper.className = 'image-zoom-icon-wrapper';
      parent.insertBefore(wrapper, imageToUpdate);
      wrapper.appendChild(imageToUpdate);
    } else if (!imageData.zoomType && hasWrapper) {
      if (!parent.parentElement) return;

      parent.parentElement.insertBefore(imageToUpdate, parent);
      parent.remove();
    }
  };

  const afterSave = (res) => {
    const imageModel = res.length ? res[0] : res;

    replaceImage(imageModel);

    const updatedImageModels = slideObject.imageModels.map(
      im => (im.id.toString() === imageModel.id.toString() ? { ...im, ...imageModel } : im),
    );
    updateSlideObject({ ...slideObject, imageModels: updatedImageModels });

    editorInstance.undo.saveStep();
  };

  const formatImageModel = (imageData) => {
    if (!imageData) return null;
    return (
      {
        id: parseInt(imageData.id, 10),
        image: imageData.image,
        image_fileName: imageData.image.fileName,
        image_fileNameEs: imageData.image.fileNameEs,
        // AIDEV-NOTE: REST API returns snake_case (zoom_type), GraphQL returns camelCase (zoomType)
        zoomType: imageData.zoomType ?? imageData.zoom_type,
      }
    );
  };

  return (
    <EditImage
      fromSlideObject
      afterMergeDuplicates={afterSave}
      afterSubmit={afterSave}
      dontMergeCallback={afterSave}
      dropdownOptions={{
        zoom_type: [
          { label: 'in/out buttons', value: 'in/out buttons' },
          { label: 'none', value: '' },
        ],
      }}
      excludeFields={['display_size', 'alignment', 'caption_en', 'caption_es']}
      getExistingImages={fetchImages}
      imageModel={formatImageModel(imgModel)}
      isAdmin={isAdmin}
      modalManager={modalManager}
      modelId={slideObject.id}
      modelType="SlideObject"
      standaloneButton={false}
    />
  );
};

export default CustomEditImageModal;
